import React from "react";
import { useChatCurrentUser } from "../contexts/chat-current-user-context";

const LIMIT = 100;

const useChatRoom = ({ roomId }) => {
  const { currentChatUser } = useChatCurrentUser();
  const [messages, setMessages] = React.useState([]);
  const [currentRoom, setCurrentRoom] = React.useState({});
  const [usersTyping, setUsersTyping] = React.useState([]);
  const [loadingRoom, setLoadingRoom] = React.useState(false);
  // this first message value is loaded to see if we need to show the "load more" indicator
  // it is separate because it may not be in messages if there are lots
  const [firstMessage, setFirstMessage] = React.useState({});

  const fetchOlderMessages = initialId => {
    if (currentChatUser.id && initialId) {
      const options = {
        roomId,
        direction: "older",
        limit: LIMIT,
        initialId,
      };

      return currentChatUser
        .fetchMultipartMessages(options)
        .then(olderMessages => {
          // older go first
          setMessages(prev => [...olderMessages, ...prev]);
        })
        .catch(err => {
          console.log(`Error fetching messages: ${err}`);
        });
    }
  };

  React.useEffect(() => {
    if (!currentChatUser.id) return;

    setLoadingRoom(true);
    currentChatUser
      .subscribeToRoomMultipart({
        roomId: roomId,
        messageLimit: 0,
        hooks: {
          onMessage: message => {
            setMessages(prev => [...prev, message]);
          },
          onUserStartedTyping: user => {
            setUsersTyping(prev => [...prev, user]);
          },
          onUserStoppedTyping: user => {
            setUsersTyping(prev => prev.filter(prevUser => prevUser.id !== user.id));
          },
          onMessageDeleted: messageId => {
            setMessages(prev => prev.filter(msg => msg.id !== messageId));
          },
        },
      })
      .then(room => {
        setCurrentRoom({ ...room, users: room.users.filter(i => i.id !== "super") });
        // init fetch only
        currentChatUser
          .fetchMultipartMessages({ direction: "older", limit: LIMIT, roomId })
          .then(messages => {
            setMessages(messages);
            setLoadingRoom(false);
          })
          .catch(err => {
            console.log(`Error fetching messages: ${err}`);
          });
      });
  }, [currentChatUser, roomId]);

  // fetch first message to see if we need to load more or not
  // NOTE: this uses 'direction: newer' to grab the first ones
  React.useEffect(() => {
    if (!currentChatUser.id) return;

    currentChatUser
      .fetchMultipartMessages({
        roomId: roomId,
        direction: "newer",
        limit: 1,
      })
      .then(messages => {
        setFirstMessage(messages[0] || {});
      })
      .catch(err => {
        console.log(`Error fetching messages: ${err}`);
      });
  }, [currentChatUser, roomId]);

  const earliest_message = messages && messages[0] ? messages[0] : {};
  const are_more_messages_to_fetch = earliest_message.createdAt > firstMessage.createdAt;
  return {
    messages,

    usersTyping,
    currentRoom,
    loadingRoom,
    firstMessage,
    fetchOlderMessages: () => fetchOlderMessages(earliest_message.id),
    areMoreMessagesToFetch: are_more_messages_to_fetch,
  };
};

export { useChatRoom };
